import {ChatNotesClient} from './client';
import type {ChatNotesDraftInput, ChatNotesNoteInput} from './client';
import {
  ChatNotesCoreStatus,
  ChatNotesAttachment,
  ChatNotesAttachmentKind,
  ChatNotesFolder,
  ChatNotesDraft,
  ChatNotesNote,
  ChatNotesNotificationSettings,
  ChatNotesNotePage,
  ChatNotesUser,
  READ_FOLDERS_CAPABILITY,
  READ_DRAFTS_CAPABILITY,
  READ_ATTACHMENTS_CAPABILITY,
  READ_NOTIFICATIONS_CAPABILITY,
  READ_NOTES_CAPABILITY,
  WRITE_FOLDERS_CAPABILITY,
  WRITE_DRAFTS_CAPABILITY,
  WRITE_ATTACHMENTS_CAPABILITY,
  WRITE_NOTES_CAPABILITY,
  WRITE_NOTIFICATIONS_CAPABILITY
} from './contract';
import {ChatNotesError} from './errors';

export interface ActiveChatNotesSession {
  expiresAt: string;
  user: ChatNotesUser;
}

interface StoredChatNotesSession {
  expiresAt: string;
  token: string;
}

const SESSION_STORAGE_KEY = 'chat-notes.core-session.v1';
const SESSION_TOKEN_PATTERN = /^cns_[A-Za-z0-9_-]{43}$/;

function readStoredSession(): StoredChatNotesSession | undefined {
  try {
    const value = globalThis.sessionStorage?.getItem(SESSION_STORAGE_KEY);
    if(!value) return undefined;
    const parsed = JSON.parse(value) as Partial<StoredChatNotesSession>;
    if(
      typeof(parsed.token) !== 'string' || !SESSION_TOKEN_PATTERN.test(parsed.token) ||
      typeof(parsed.expiresAt) !== 'string' || Date.parse(parsed.expiresAt) <= Date.now()
    ) {
      globalThis.sessionStorage?.removeItem(SESSION_STORAGE_KEY);
      return undefined;
    }
    return {token: parsed.token, expiresAt: parsed.expiresAt};
  } catch {
    return undefined;
  }
}

function writeStoredSession(session?: StoredChatNotesSession): void {
  try {
    if(session) globalThis.sessionStorage?.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    else globalThis.sessionStorage?.removeItem(SESSION_STORAGE_KEY);
  } catch {}
}

export interface ChatNotesCompatibilityAdapter {
  connect(): Promise<ChatNotesCoreStatus>;
  authenticateTelegram(initData: string): Promise<ActiveChatNotesSession>;
  authenticateDevelopment(): Promise<ActiveChatNotesSession>;
  getSession(): Promise<ActiveChatNotesSession | null>;
  revokeSession(): Promise<void>;
  listFolders(): Promise<ChatNotesFolder[]>;
  createFolder(input: {
    title: string;
    icon?: string;
    preferredFolderNumber?: number;
  }): Promise<ChatNotesFolder>;
  updateFolder(
    folderId: string,
    input: {title: string; icon?: string; revision: number}
  ): Promise<ChatNotesFolder>;
  deleteFolder(folderId: string): Promise<void>;
  reorderFolders(folderIds: string[]): Promise<ChatNotesFolder[]>;
  listNotes(options?: {folderId?: string; cursor?: string; limit?: number}): Promise<ChatNotesNotePage>;
  getNote(noteId: string): Promise<ChatNotesNote>;
  createNote(input: ChatNotesNoteInput): Promise<ChatNotesNote>;
  updateNote(noteId: string, input: ChatNotesNoteInput & {revision: number}): Promise<ChatNotesNote>;
  deleteNote(noteId: string): Promise<void>;
  listDrafts(): Promise<ChatNotesDraft[]>;
  saveDraft(input: ChatNotesDraftInput): Promise<void>;
  getNotificationSettings(scope: string): Promise<ChatNotesNotificationSettings>;
  saveNotificationSettings(
    input: Pick<ChatNotesNotificationSettings, 'scope' | 'showPreviews' | 'muteUntil'>
  ): Promise<ChatNotesNotificationSettings>;
  uploadAttachment(kind: ChatNotesAttachmentKind, file: Blob, fileName: string): Promise<ChatNotesAttachment>;
  getAttachment(attachmentId: string): Promise<ChatNotesAttachment>;
  downloadAttachment(attachmentId: string): Promise<Uint8Array>;
}

export function createChatNotesCompatibilityAdapter(
  client: ChatNotesClient
): ChatNotesCompatibilityAdapter {
  let token: string | undefined;
  let activeSession: ActiveChatNotesSession | null = null;
  let coreStatus: ChatNotesCoreStatus | undefined;

  const requireToken = () => {
    if(!token) {
      throw new ChatNotesError('SESSION_REQUIRED', 'A Chat Notes session is required.');
    }
    return token;
  };

  const requireCapability = (capability: string) => {
    if(!coreStatus?.integration.capabilities.includes(capability)) {
      throw new ChatNotesError(
        'INCOMPATIBLE_PROTOCOL',
        `Chat Notes Core does not provide ${capability}.`
      );
    }
  };

  return {
    async connect() {
      coreStatus = await client.getStatus();
      return coreStatus;
    },
    async authenticateTelegram(initData) {
      const stored = readStoredSession();
      if(stored) {
        try {
          const current = await client.getSession(stored.token);
          token = stored.token;
          activeSession = {expiresAt: stored.expiresAt, user: current.user};
          return activeSession;
        } catch(error) {
          if(!(error instanceof ChatNotesError) || error.status !== 401) throw error;
          writeStoredSession();
        }
      }
      const session = await client.createTelegramSession(initData);
      token = session.token;
      activeSession = {expiresAt: session.expiresAt, user: session.user};
      writeStoredSession({token: session.token, expiresAt: session.expiresAt});
      return activeSession;
    },
    async authenticateDevelopment() {
      const session = await client.createDevelopmentSession();
      token = session.token;
      activeSession = {expiresAt: session.expiresAt, user: session.user};
      return activeSession;
    },
    async getSession() {
      if(!token || !activeSession) return null;
      const current = await client.getSession(token);
      activeSession = {...activeSession, user: current.user};
      return activeSession;
    },
    async revokeSession() {
      if(token) await client.revokeSession(token);
      token = undefined;
      activeSession = null;
      writeStoredSession();
    },
    async listFolders() {
      requireCapability(READ_FOLDERS_CAPABILITY);
      return await client.listFolders(requireToken());
    },
    async createFolder(input) {
      requireCapability(WRITE_FOLDERS_CAPABILITY);
      return await client.createFolder(requireToken(), input);
    },
    async updateFolder(folderId, input) {
      requireCapability(WRITE_FOLDERS_CAPABILITY);
      return await client.updateFolder(requireToken(), folderId, input);
    },
    async deleteFolder(folderId) {
      requireCapability(WRITE_FOLDERS_CAPABILITY);
      await client.deleteFolder(requireToken(), folderId);
    },
    async reorderFolders(folderIds) {
      requireCapability(WRITE_FOLDERS_CAPABILITY);
      return await client.reorderFolders(requireToken(), folderIds);
    },
    async listNotes(options) {
      requireCapability(READ_NOTES_CAPABILITY);
      return await client.listNotes(requireToken(), options);
    },
    async getNote(noteId) {
      requireCapability(READ_NOTES_CAPABILITY);
      return await client.getNote(requireToken(), noteId);
    },
    async createNote(input) {
      requireCapability(WRITE_NOTES_CAPABILITY);
      return await client.createNote(requireToken(), input);
    },
    async updateNote(noteId, input) {
      requireCapability(WRITE_NOTES_CAPABILITY);
      return await client.updateNote(requireToken(), noteId, input);
    },
    async deleteNote(noteId) {
      requireCapability(WRITE_NOTES_CAPABILITY);
      await client.deleteNote(requireToken(), noteId);
    },
    async listDrafts() {
      requireCapability(READ_DRAFTS_CAPABILITY);
      return await client.listDrafts(requireToken());
    },
    async saveDraft(input) {
      requireCapability(WRITE_DRAFTS_CAPABILITY);
      await client.saveDraft(requireToken(), input);
    },
    async getNotificationSettings(scope) {
      requireCapability(READ_NOTIFICATIONS_CAPABILITY);
      return client.getNotificationSettings(requireToken(), scope);
    },
    async saveNotificationSettings(input) {
      requireCapability(WRITE_NOTIFICATIONS_CAPABILITY);
      return client.saveNotificationSettings(requireToken(), input);
    },
    async uploadAttachment(kind, file, fileName) {
      requireCapability(WRITE_ATTACHMENTS_CAPABILITY);
      return await client.uploadAttachment(requireToken(), kind, file, fileName);
    },
    async getAttachment(attachmentId) {
      requireCapability(READ_ATTACHMENTS_CAPABILITY);
      return await client.getAttachment(requireToken(), attachmentId);
    },
    async downloadAttachment(attachmentId) {
      requireCapability(READ_ATTACHMENTS_CAPABILITY);
      return await client.downloadAttachment(requireToken(), attachmentId);
    }
  };
}
